import type { PriorFinding } from '../../../api/questionQualityApi';
import { sevColor, verdictColor } from './verdictDisplay';
import { ResolutionBadge } from './ResolutionEditor';
import { AiFixCard } from './AiFixCard';
import './QuestionQuality.css';

/**
 * Where this finding has been before — one entry per version of the question a
 * rule has judged.
 *
 * The reviewer's real question is rarely "what does the rule say", which is on
 * screen already; it is "has anyone been here before, and what did they do".
 * Until the ledger kept these, the honest answer was to read the version history
 * and infer it: the verdict document is replaced on every re-judge, so a
 * confirmed fix erased the resolution that closed it and a FAILED fix came back
 * looking like a finding nobody had ever seen.
 *
 * Collapsed, because a finding usually has no history and one that does is
 * asking for a decision about the present. Newest first, matching the order the
 * recorder stores them in.
 *
 * See docs/FeatureDesigns/question-quality/checks-target.md §11a.
 */
export function FindingHistory({ prior, dropped }: { prior: PriorFinding[]; dropped?: number }) {
    if (!prior.length) return null;

    const tried = prior.filter(p => p.aiFix).length;

    return (
        <details className="qq-history">
            <summary>
                Been here before ({prior.length}
                {dropped ? ` of ${prior.length + dropped}` : ''})
                {/* The one thing worth saying without opening it: an AI fix that
                    did not hold is the case this whole record exists for. */}
                {tried > 0 && <span className="qq-dim"> · {tried} AI fix{tried > 1 ? 'es' : ''} attempted</span>}
            </summary>

            {prior.map((p, i) => (
                <article key={`${p.checkRunId}-${p.contentHash}-${i}`} className="qq-history-entry">
                    <header>
                        <span style={{ color: p.verdict === 'fail' ? sevColor[p.severity] : verdictColor[p.verdict] }}>
                            {p.verdict === 'fail' ? `fail · ${p.severity}` : p.verdict}
                        </span>
                        {p.resolution && <ResolutionBadge status={p.resolution.status} />}
                        <span className="qq-dim">
                            {new Date(p.checkedAt).toLocaleDateString()}
                            {/* The rule version, because a verdict under an older
                                rubric was answering a different question. */}
                            {' · '}{p.ruleVersion}
                        </span>
                    </header>

                    <p className="qq-finding-why">{p.rationale}</p>

                    {p.resolution && (
                        <p className="qq-dim">
                            {p.resolution.comment ? `“${p.resolution.comment}” — ` : ''}
                            {p.resolution.byName ?? p.resolution.by?.slice(0, 8) ?? 'someone'}
                            {', '}{new Date(p.resolution.at).toLocaleDateString()}
                        </p>
                    )}

                    {p.aiFix && <AiFixCard fix={p.aiFix} />}
                </article>
            ))}

            {/* A cap that hides its own truncation is how a ledger starts
                disagreeing with itself about how many times a question has been
                round. */}
            {!!dropped && (
                <p className="qq-dim">
                    {dropped} older cycle{dropped > 1 ? 's' : ''} no longer kept.
                </p>
            )}
        </details>
    );
}
